import { GetSearchRequestsModel, ISearchHistoryService, IServiceTransactionManager, Listing, SearchRequestBase } from '@truckdown/locations';
import { ClientRestrictionStatus, ILookupService, IRestrictionManager, RegionModel, ServiceModel } from '@truckdown/systems';
import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, computed, onMounted } from 'vue';

interface IData {
    panel: Ref<string>,
    markerToOpen: Ref<string>,
    searchCount: Ref<number>,
    callCount: Ref<number>,
    status: Ref<ClientRestrictionStatus>,
    regions: Ref<RegionModel[]>
}

const getData = function (openMap: boolean) {
    return {
        panel: ref<string>(openMap ? 'map' : ''),
        markerToOpen: ref<string>(''),
        searchCount: ref<number>(0),
        callCount: ref<number>(0),
        status: ref<ClientRestrictionStatus>(ClientRestrictionStatus.None),
        regions: ref<RegionModel[]>([])
    } as IData;
}

const getComputed = function (data: IData) {
    return {
        statusText: computed(() => {
            switch (data.status.value) {
                case ClientRestrictionStatus.Blocked: {
                    return 'Blocked';
                }

                case ClientRestrictionStatus.Captcha: {
                    return 'Captcha';
                }

                default: {
                    return 'None';
                }
            }
        })
    };
}

const setRegions = function (data: IData) {
    context.getService<ILookupService>('ILookupService')
        .then((service) => {
            return service
                .getRegions()
                .then((res) => {
                    data.regions.value = res;
                });
        })
        .catch((reason) => {
            console.error('Error getting regions', reason);
        });
}

const setStatus = function (data: IData) {
    context.getService<IRestrictionManager>('IRestrictionManager')
        .then((service) => {
            return service
                .getStatusWithThrottle('search')
                .then((res) => {
                    data.status.value = res;
                });
        })
        .catch((reason) => {
            console.error('Error getting restriction status', reason);
        });
}

const updateCounts = function (data: IData) {
    if (context.isAuthenticated) {
        context.getService<ISearchHistoryService>('ISearchHistoryService')
            .then((service) => {
                return service
                    .getRequestHistoryCount()
                    .then((res) => {
                        data.searchCount.value = res;
                    });
            })
            .catch((reason) => {
                console.error('Error getting search history count', reason);
            });

        context.getService<IServiceTransactionManager>('IServiceTransactionManager')
            .then((service) => {
                return service
                    .getTransactionsCount()
                    .then((res) => {
                        data.callCount.value = res;
                    });
            })
            .catch((reason) => {
                console.error('Error getting call history count', reason);
            });
    }
}

const getMethods = function (data: IData) {
    return {
        showMap: function (ev: Event) {
            if (data.panel.value == 'map') {
                data.panel.value = '';
            } else {
                data.markerToOpen.value = '';
                data.panel.value = 'map';
            }
        },
        showSearch: function (ev: Event) {
            if (data.panel.value == 'search') {
                data.panel.value = '';
            } else {
                data.panel.value = 'search';
            }
        },
        showSearchHistory: function (ev: Event) {
            if (data.panel.value == 'searchHistory') {
                data.panel.value = '';
            } else {
                data.panel.value = 'searchHistory';
            }
        },
        showCallHistory: function (ev: Event) {
            if (data.panel.value == 'callHistory') {
                data.panel.value = '';
            } else {
                data.panel.value = 'callHistory';
            }
        }
    };
}

export default defineComponent({
    props: {
        services: {
            type: Array as () => ServiceModel[],
            required: true
        },
        listings: {
            type: Array as () => Listing[],
            required: true
        },
        request: {
            type: Object as () => SearchRequestBase,
            required: true
        },
        openMap: Boolean
    },
    emits: [],
    setup: function (props, ctx) {
        let data = getData(props.openMap);
        let computed = getComputed(data);
        let methods = getMethods(data);

        onMounted(() => {
            setRegions(data);
            setStatus(data);
            updateCounts(data);
        });


        context.events.on('add-call-count', function () {
            data.callCount.value = data.callCount.value + 1;
        });

        context.events.on('add-map-listings', function (listings: Listing[]) {
            props.listings.push(...listings);
        });

        context.events.on('show-map', function (id: string) {
            if (data.panel.value != 'map') {
                data.markerToOpen.value = id;
                data.panel.value = 'map';
            }
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});
